import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/CustomTable'
import { useBillsQuery } from '@/hooks/useBills'
import type { ColumnDef } from '@tanstack/react-table'
import type { Bill } from '@/types/backend'
import { formatCFA, cn, formatDateFR } from '@/lib/utils'
import { Eye, Printer, Download, Wallet } from 'lucide-react'
import Loading from '@/components/Loading'
import { useTableUrlState } from '@/hooks/useTableUrlState'
import { useDebounce } from '@/hooks/useDebounce'

export default function BillsPage() {
  const navigate = useNavigate()
  const {
    state: { search, pageIndex, pageSize },
    setSearch,
    setPageIndex,
    setPageSize,
  } = useTableUrlState('bills', { search: '', pageIndex: 0, pageSize: 20 })

  const debouncedSearch = useDebounce(search, 400)

  const { data, status, isFetching } = useBillsQuery({
    q: debouncedSearch || undefined,
    page: pageIndex + 1,
    pageSize,
  })

  const tableProps = {
    searchValue: search,
    onSearchChange: setSearch,
    currentPageIndex: pageIndex,
    currentPageSize: pageSize,
    onPageChange: setPageIndex,
    onPageSizeChange: setPageSize,
    paginationServer: true as const,
    sortingServer: false as const,
  }

  const items = data?.items ?? []
  const total = data?.pagination.total ?? 0
  const pageCount = useMemo(
    () =>
      Math.max(1, data?.pagination.pageCount ?? Math.ceil(total / pageSize)),
    [data?.pagination?.pageCount, total, pageSize]
  )

  const statusBadge = (s: Bill['status']) => {
    const map: Record<Bill['status'], { label: string; className: string }> = {
      DRAFT: { label: 'Brouillon', className: 'bg-muted text-foreground' },
      ISSUED: {
        label: 'Émise',
        className:
          'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
      },
      PARTIALLY_PAID: {
        label: 'Part. payée',
        className:
          'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
      },
      PAID: {
        label: 'Payée',
        className:
          'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
      },
      VOID: {
        label: 'Annulée',
        className:
          'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
      },
    }
    const cfg = map[s]
    return (
      <Badge className={cn('rounded-full px-2 py-0.5 text-xs', cfg.className)}>
        {cfg.label}
      </Badge>
    )
  }

  const columns = useMemo<ColumnDef<Bill, unknown>[]>(
    () => [
      {
        accessorKey: 'incurredOn',
        header: 'Date',
        cell: ({ row }) => {
          const d = row.original.incurredOn || row.original.createdAt
          return formatDateFR(d)
        },
      },
      {
        accessorKey: 'customer.name',
        header: 'Client',
        cell: ({ row }) => row.original.customer?.name ?? '—',
      },
      {
        accessorKey: 'status',
        header: 'Statut',
        cell: ({ row }) => statusBadge(row.original.status),
      },
      {
        accessorKey: 'total',
        header: () => <div className=''>Total</div>,
        cell: ({ row }) => (
          <div className=' tabular-nums'>{formatCFA(row.original.total)}</div>
        ),
      },
      {
        accessorKey: 'paid',
        header: () => <div className=''>Payé</div>,
        cell: ({ row }) => (
          <div className=' tabular-nums text-emerald-600'>
            {formatCFA(row.original.paid)}
          </div>
        ),
      },
      {
        accessorKey: 'balance',
        header: () => <div className=''>Restant</div>,
        cell: ({ row }) => (
          <div
            className={cn(
              ' tabular-nums',
              row.original.balance > 0
                ? 'text-red-600'
                : row.original.balance < 0
                ? 'text-emerald-600'
                : 'text-muted-foreground'
            )}
          >
            {formatCFA(row.original.balance)}
          </div>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => {
          const b = row.original
          const go = (path: string) => (e: React.MouseEvent) => {
            e.stopPropagation()
            navigate(path)
          }
          return (
            <div className='flex gap-2'>
              <Button
                variant='ghost'
                size='icon'
                title='Voir'
                onClick={go(`/bills/${b.id}`)}
              >
                <Eye className='h-4 w-4' />
              </Button>
              <Button
                variant='ghost'
                size='icon'
                title='Imprimer'
                onClick={go(`/bills/${b.id}?action=print`)}
              >
                <Printer className='h-4 w-4' />
              </Button>
              <Button
                variant='ghost'
                size='icon'
                title='Télécharger PDF'
                onClick={go(`/bills/${b.id}?action=pdf`)}
              >
                <Download className='h-4 w-4' />
              </Button>
              <Button
                variant='ghost'
                size='icon'
                title={b.status === 'VOID' ? 'Facture annulée' : 'Paiements'}
                onClick={go(`/bills/${b.id}?action=pay#payments`)}
                disabled={b.status === 'VOID'}
              >
                <Wallet className='h-4 w-4' />
              </Button>
            </div>
          )
        },
      },
    ],
    [navigate]
  )

  if (status === 'pending') return <Loading />

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between gap-3'>
        <h3 className='text-2xl font-semibold'>Factures</h3>
      </div>

      <DataTable<Bill, unknown>
        columns={columns}
        data={items}
        {...tableProps}
        total={total}
        pageCount={pageCount}
        searchValue={search}
        onSearchChange={setSearch}
        rowClickable
        onRowClick={(row) => navigate(`/bills/${row.id}`)}
      />

      <div className='text-xs text-muted-foreground'>
        {isFetching ? 'Actualisation…' : null}
      </div>
    </div>
  )
}
